import { ComponentColor } from "@common/types";
import { LocationStatus } from "@models/location";
import {
    LocationActivationOutcome,
    OrganizationStatus,
    type LocationApprovalResult,
} from "@models/organization";
import { asApiFailure, wasReported } from "@utils/apiFailure";
import { describeApprovalResult } from "./approvalOutcome";

// The New Location drawer (C4, API #427): an admin adds a location to a client
// and approves it in the same step.

// Why a client can't take a new location, or null when it can. The API
// refuses a suspended or cancelled client with a 409, so the page says so on
// the control rather than on submit.
export const addLocationBlockedReason = (
    status: OrganizationStatus,
): string | null => {
    switch (status) {
        case OrganizationStatus.Suspended:
            return "A location can't be added while this client is suspended.";
        case OrganizationStatus.Cancelled:
            return "A location can't be added to a cancelled client.";
        default:
            return null;
    }
};

// What a failed add means:
// - gone: the API's own 404, the client is gone (or was soft-deleted);
// - client_closed: the 409, the client is suspended or cancelled;
// - not_added: any other 4xx. The API refuses before it writes anything, and
//   a 4xx it did not author never reached it;
// - unknown: a 5xx or any failure without an HTTP status. A 500 can come
//   after the location was saved (a save that committed and then reported a
//   failure), and a request that lost its answer may have landed, so it may
//   exist. A failure before the request was sent (no token) lands here too:
//   saying it may exist is the safe reading where money is involved.
export type AddLocationRefusalKind =
    | "gone"
    | "client_closed"
    | "not_added"
    | "unknown";

// How the drawer hands the page what happened, when the page has to say it.
export type AddLocationDone =
    | { kind: "added"; result: LocationApprovalResult }
    | { kind: "client_closed" }
    | { kind: "unknown" }
    | { kind: "gone" };

// The page shows "gone" as its client-gone view, and the rest above the list.
export type AddLocationOutcomeState = Exclude<AddLocationDone, { kind: "gone" }>;

// useOrganizationApi toasts every failure but the 409 and the API's own 404,
// with the server's words, so the not-added and may-be-added lines name no
// cause. The 409 is not toasted, so the client-closed line names its cause.
export const NOT_ADDED_MESSAGE =
    "The location wasn't added. Your details are still here, so you can try again.";
export const CLIENT_CLOSED_MESSAGE =
    "No location was added: this client was suspended or cancelled after the page loaded.";
export const MAY_BE_ADDED_MESSAGE =
    "We couldn't tell whether the location was added. Check the Locations list before adding it again.";

export const addLocationRefusal = (error: unknown): AddLocationRefusalKind => {
    const status = asApiFailure(error)?.status;

    // The API's own 404 is the one useOrganizationApi did not toast (it
    // carried the API's body).
    if (status === 404 && !wasReported(error)) {
        return "gone";
    }

    if (status === 409) {
        return "client_closed";
    }

    if (status !== undefined && status >= 400 && status < 500) {
        return "not_added";
    }

    return "unknown";
};

export interface AddedLocationView {
    color: ComponentColor.Success | ComponentColor.Warning | ComponentColor.Error;
    title: string;
    detail: string | null;
}

const NOT_APPROVED_TITLE = "Location added, but not approved.";
const CHECK_STATUS = "Check its status in the list below.";

// What a success's charge did. Fails closed too (PR 441 review L1): only
// NotAttempted says nothing was charged; an outcome it can't read says to
// check the list.
const chargeSentence = (outcome: LocationActivationOutcome): string => {
    switch (outcome) {
        case LocationActivationOutcome.Activated:
            return "The client's card on file was charged for it.";
        case LocationActivationOutcome.NotAttempted:
            return "Nothing was charged yet: the client is charged for it when they add a card or their membership renews.";
        default:
            return CHECK_STATUS;
    }
};

// What the 201 means to the admin. It fails CLOSED, as describeApprovalResult
// does: only `approved` together with an Approved status is a success.
// - The API's sentence (`error`) is set only when this request did not finish
//   the approval: it failed after the location was saved, or another admin
//   reviewed it at the same moment. It is said as given.
// - A PendingPayment location's charge did not go through: the approval
//   screen's words for each outcome.
// - Anything else says to check the list.
export const describeAddedLocation = (
    result: LocationApprovalResult,
): AddedLocationView => {
    if (result.approved && result.status === LocationStatus.Approved) {
        return {
            color: ComponentColor.Success,
            title: "Location added and approved.",
            detail: chargeSentence(result.activationOutcome),
        };
    }

    const apiSentence = result.error?.trim();
    if (apiSentence) {
        return {
            color: ComponentColor.Error,
            title: NOT_APPROVED_TITLE,
            detail: apiSentence,
        };
    }

    if (result.status === LocationStatus.PendingPayment) {
        const view = describeApprovalResult(result);

        return {
            color:
                view.color === ComponentColor.Warning
                    ? ComponentColor.Warning
                    : ComponentColor.Error,
            title: NOT_APPROVED_TITLE,
            detail: view.message ?? CHECK_STATUS,
        };
    }

    return {
        color: ComponentColor.Error,
        title: NOT_APPROVED_TITLE,
        detail: CHECK_STATUS,
    };
};
